/**
 * Recurrence editor logic per REQ-036:
 *   "Staff UI offers a recurrence editor matching Google Calendar's pattern
 *    (in-line dropdown, expanded panel for advanced fields,
 *    `this` / `this_and_future` / `all` scope picker on edit)."
 *
 * The frequency presets and the scope-dispatch payload are pure functions; the
 * RecurrenceEditor component composes them into the UI.
 */

export type Frequency = "DAILY" | "WEEKLY" | "MONTHLY" | "YEARLY";
export type RecurrenceScope = "this" | "this_and_future" | "all";

export interface RecurrencePreset {
  label: string;
  rrule: string;
}

const WEEKDAY_BY_INDEX = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;

/**
 * Google Calendar dropdown presets, derived from a starting date so e.g.
 * "Weekly on Tuesday" reflects the appointment's actual weekday.
 */
export function presetsForDate(iso: string): RecurrencePreset[] {
  const date = new Date(iso);
  const weekday = WEEKDAY_BY_INDEX[date.getDay()];
  const monthDay = date.getDate();
  return [
    { label: "Does not repeat", rrule: "" },
    { label: "Daily", rrule: "FREQ=DAILY" },
    { label: `Weekly on ${weekdayLabel(weekday)}`, rrule: `FREQ=WEEKLY;BYDAY=${weekday}` },
    {
      label: `Monthly on day ${monthDay}`,
      rrule: `FREQ=MONTHLY;BYMONTHDAY=${monthDay}`
    },
    { label: "Annually", rrule: "FREQ=YEARLY" },
    { label: "Every weekday (M-F)", rrule: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR" }
  ];
}

function weekdayLabel(code: (typeof WEEKDAY_BY_INDEX)[number]): string {
  return {
    SU: "Sunday",
    MO: "Monday",
    TU: "Tuesday",
    WE: "Wednesday",
    TH: "Thursday",
    FR: "Friday",
    SA: "Saturday"
  }[code];
}

/**
 * Build the request body for PATCH /recurrence-series/{id}.
 *
 * Per PRD §"Flow E" + REQ-018, scope=this writes an EXDATE + new one-off,
 * scope=this_and_future truncates with UNTIL and creates a new series, scope=all
 * rewrites the spec. The web only conveys the scope and the new RRULE; the API
 * handles reconciliation.
 */
export function buildUpdatePayload(input: {
  scope: RecurrenceScope;
  rrule?: string;
  fromInstanceAppointmentId?: string;
}): { scope: RecurrenceScope; rrule?: string; from_instance_appointment_id?: string } {
  if (input.scope === "all") return { scope: "all", rrule: input.rrule };
  if (!input.fromInstanceAppointmentId) {
    throw new Error("scope=this and scope=this_and_future require an instance id");
  }
  return {
    scope: input.scope,
    rrule: input.rrule,
    from_instance_appointment_id: input.fromInstanceAppointmentId
  };
}

/**
 * Validate user input against the scope. The API enforces this server-side; the
 * web pre-empts with a friendlier error.
 */
export function validateScopeChange(input: { scope: RecurrenceScope; rrule?: string }): { ok: true } | { ok: false; reason: string } {
  if (input.scope !== "all" && input.rrule) {
    return { ok: false, reason: "Changing the rule itself is only allowed with scope=all." };
  }
  return { ok: true };
}
